'use client';
import { twMerge } from 'tailwind-merge';
import { useUploadManager } from '@/hooks/use-upload-manager';
import { useCallback, useEffect, useRef, useState } from 'react';

export function FileUploader({
  className,
  containerClassName,
  defaultValues,
  values: valuesProp,
  onChange,
  prepareUpload,
  deleteFile,
  multiple = false,
  accept,
  renderPreviews,
  renderProgress,
  renderValues,
  placeholder,
  keepPlaceholder = false,
  noClick = false
}) {
  const areaRef = useRef(null);
  const [isOver, setIsOver] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const {
    selectFiles,
    uploadFiles,
    clear,
    progress,
    values,
    previews,
    isBusy
  } = useUploadManager({
    defaultValues,
    values: valuesProp,
    onChange,
    prepareUpload,
    deleteFile,
    multiple,
    accept
  });

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      (async () => {
        if (multiple) {
          uploadFiles(e.dataTransfer.files);
        } else {
          // No multiples. Clear out the old one if necessary
          await clear(0);
          uploadFiles([e.dataTransfer.files[0]]);
        }
      })();
      setIsOver(false);
      setIsDragging(false);
    },
    [uploadFiles, multiple, clear]
  );

  useEffect(() => {
    const handleDragOver = (e) => {
      if (e.target === areaRef.current) {
        e.preventDefault();
      }
    };
    const handleDragIn = (e) => {
      if (e.relatedTarget === null) {
        e.preventDefault();
        setIsDragging(true);
      }
      if (e.target === areaRef.current) {
        setIsOver(true);
      }
    };
    const handleDragOut = (e) => {
      if (e.target === areaRef.current) {
        setIsOver(false);
      }
      if (e.relatedTarget === null) {
        setIsDragging(false);
      }
    };
    const handleWindowDrop = () => {
      setIsDragging(false);
    };
    if (typeof document !== 'undefined') {
      window.addEventListener('dragenter', handleDragIn);
      window.addEventListener('dragleave', handleDragOut);
      window.addEventListener('drop', handleWindowDrop);
    }
    let cleanupAreaRef;
    if (areaRef.current) {
      cleanupAreaRef = areaRef.current;
      areaRef.current.addEventListener('dragenter', handleDragOver);
      areaRef.current.addEventListener('dragover', handleDragOver);
      areaRef.current.addEventListener('drop', handleDrop);
    }
    return () => {
      if (cleanupAreaRef) {
        cleanupAreaRef.removeEventListener('dragenter', handleDragOver);
        cleanupAreaRef.removeEventListener('dragover', handleDragOver);
        cleanupAreaRef.removeEventListener('drop', handleDrop);
      }
      if (typeof document !== 'undefined') {
        window.removeEventListener('dragenter', handleDragIn);
        window.removeEventListener('dragleave', handleDragOut);
        window.removeEventListener('drop', handleWindowDrop);
      }
    };
  }, [handleDrop]);

  return (
    <div className={containerClassName}>
      {(keepPlaceholder || !previews.length) && (
        <div
          className={twMerge(
            'relative outline-none w-full h-full',
            !noClick && 'cursor-pointer',
            className
          )}
          ref={areaRef}
          role={!noClick ? 'button' : null}
          onClick={
            !noClick
              ? (e) => {
                  if (e.target === areaRef.current) {
                    e.stopPropagation();
                    selectFiles();
                  }
                }
              : null
          }
          tabIndex={!noClick ? 0 : -1}
          onKeyUp={
            !noClick
              ? (e) => {
                  if (
                    (e.target === areaRef.current && e.key === 'Enter') ||
                    e.key === ' '
                  ) {
                    e.stopPropagation();
                    e.preventDefault();
                    selectFiles();
                  }
                }
              : null
          }
        >
          {typeof placeholder === 'function' && (
            <div
              className={twMerge(
                'contents',
                (!noClick || isDragging) && 'pointer-events-none'
              )}
            >
              {placeholder(selectFiles)}
            </div>
          )}
          {typeof placeholder === 'string' && (
            <div
              className={twMerge(
                'contents',
                (!noClick || isDragging) && 'pointer-events-none'
              )}
            >
              {placeholder}
            </div>
          )}
          <svg
            className={twMerge(
              'w-full h-full inset-0 absolute pointer-events-none rounded-[inherit] overflow-hidden hidden opacity-70',
              isDragging && 'block',
              isOver && 'opacity-100'
            )}
            preserveAspectRatio="none"
            style={{
              '--antsize': '8px',
              '--antspeed': isOver ? '0.1s' : '0.5s'
            }}
          >
            <rect
              className="marching-ants [rx:var(--rounded-box)]"
              width="100%"
              height="100%"
              fill="oklch(var(--p)/0.2)"
              stroke="oklch(var(--p))"
            />
          </svg>
        </div>
      )}
      {!!values.length && renderValues?.(values)}
      {!!previews.length && renderPreviews && renderPreviews(previews, clear)}
      {isBusy && renderProgress?.(progress)}
    </div>
  );
}
